// Premium Schema.org markup library for X67 Digital — 2026
// Maximizes rich-snippet eligibility across Google, Bing, Perplexity, ChatGPT, Claude

const SchemaMarkup = ({ data }) => {
  if (!data) return null;
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

const BASE_URL = "https://x67digital.com";

// ============== SITE-LEVEL ==============
export function WebsiteSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${BASE_URL}#website`,
    "name": "X67 Digital Media",
    "alternateName": ["X67", "X67 Digital", "X67 Anunturi", "X67 Marketplace"],
    "url": BASE_URL,
    "description": "Cel mai mare site de anunțuri gratuite din România cu licitații live, escrow integrat și AI vision pentru anunțuri",
    "inLanguage": "ro-RO",
    "potentialAction": [
      {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${BASE_URL}/category/all?search={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    ],
    "publisher": {
      "@id": `${BASE_URL}#organization`
    }
  };
  return <SchemaMarkup data={data} />;
}

export function OrganizationSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${BASE_URL}#organization`,
    "name": "X67 Digital Media Groupe",
    "alternateName": "X67 Digital",
    "legalName": "X67 Digital Media SRL",
    "url": BASE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/logo192.png`,
      "width": 192,
      "height": 192
    },
    "image": `${BASE_URL}/og-image.png`,
    "description": "Marketplace românesc cu licitații live, escrow integrat, AI vision pentru anunțuri și verificare KYC",
    "foundingDate": "2024",
    "areaServed": {
      "@type": "Country",
      "name": "România",
      "alternateName": "Romania"
    },
    "knowsLanguage": ["ro", "en"],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "RO",
      "addressLocality": "București"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "email": "contact@x67digital.com",
        "contactType": "customer service",
        "areaServed": "RO",
        "availableLanguage": ["ro", "en"]
      },
      {
        "@type": "ContactPoint",
        "email": "support@x67digital.com",
        "contactType": "technical support",
        "areaServed": "RO",
        "availableLanguage": ["ro"]
      }
    ],
    "sameAs": [
      "https://x67digital.com",
      "https://www.facebook.com/profile.php?id=61588030305324",
      "https://www.instagram.com/x67digital",
      "https://twitter.com/x67digital"
    ]
  };
  return <SchemaMarkup data={data} />;
}

// LocalBusiness for hyper-local Romanian SEO
export function LocalBusinessSchema({ city = "România" }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}#local-${city.toLowerCase()}`,
    "name": `X67 Digital Media — ${city}`,
    "image": `${BASE_URL}/og-image.png`,
    "url": BASE_URL,
    "telephone": "+40-700-000-000",
    "priceRange": "Gratuit",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "RO",
      "addressLocality": city
    },
    "areaServed": city,
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    }
  };
  return <SchemaMarkup data={data} />;
}

// SiteNavigationElement (helps Google understand site hierarchy for AEO)
export function SiteNavigationSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Navigare principală X67 Digital",
    "itemListElement": [
      { "@type": "SiteNavigationElement", "position": 1, "name": "Auto", "url": `${BASE_URL}/category/auto` },
      { "@type": "SiteNavigationElement", "position": 2, "name": "Imobiliare", "url": `${BASE_URL}/category/imobiliare` },
      { "@type": "SiteNavigationElement", "position": 3, "name": "Electronice", "url": `${BASE_URL}/category/electronice` },
      { "@type": "SiteNavigationElement", "position": 4, "name": "Locuri de muncă", "url": `${BASE_URL}/category/locuri_munca` },
      { "@type": "SiteNavigationElement", "position": 5, "name": "Modă", "url": `${BASE_URL}/category/moda` },
      { "@type": "SiteNavigationElement", "position": 6, "name": "Servicii", "url": `${BASE_URL}/category/servicii` },
      { "@type": "SiteNavigationElement", "position": 7, "name": "Animale", "url": `${BASE_URL}/category/animale` },
      { "@type": "SiteNavigationElement", "position": 8, "name": "Licitații Arena", "url": `${BASE_URL}/auctions` },
      { "@type": "SiteNavigationElement", "position": 9, "name": "Blog", "url": `${BASE_URL}/blog` },
      { "@type": "SiteNavigationElement", "position": 10, "name": "Forum", "url": `${BASE_URL}/forum` }
    ]
  };
  return <SchemaMarkup data={data} />;
}

// ============== AD / PRODUCT ==============
export function AdSchema({ ad }) {
  if (!ad) return null;
  const currency = ad.currency || "RON";
  const isFree = ad.price_type === "free";

  // ---- Shipping details (Google requires shippingDetails on Offer for Merchant Listings) ----
  // If the seller set a custom rate (ad.shipping_rates from backend), use it.
  // Otherwise fall back to generic 0 RON country-wide (safe default so Google
  // doesn't flag the offer as incomplete).
  const sr = ad.shipping_rates;
  const shippingValue = sr && typeof sr.rate === "number" ? sr.rate : 0;
  const shippingCurrency = (sr && sr.currency) || currency;
  const shippingDetails = {
    "@type": "OfferShippingDetails",
    "name": sr?.carrier ? `Livrare ${sr.carrier}` : "Livrare națională",
    "shippingRate": {
      "@type": "MonetaryAmount",
      "value": shippingValue,
      "currency": shippingCurrency
    },
    "shippingDestination": {
      "@type": "DefinedRegion",
      "addressCountry": "RO"
    },
    "deliveryTime": {
      "@type": "ShippingDeliveryTime",
      "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 2, "unitCode": "DAY" },
      "transitTime":  { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 5, "unitCode": "DAY" }
    }
  };

  // ---- Merchant return policy (Google requires hasMerchantReturnPolicy on Offer) ----
  // Classifieds are between private parties — returns are at the seller's discretion.
  // We expose a valid "NoReturns" policy which Google accepts as long as the field is present.
  const hasMerchantReturnPolicy = {
    "@type": "MerchantReturnPolicy",
    "applicableCountry": "RO",
    "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted"
  };

  const offerData = {
    "@type": "Offer",
    "price": isFree ? 0 : (ad.price || 0),
    "priceCurrency": currency,
    "priceValidUntil": new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    "availability": "https://schema.org/InStock",
    "url": `${BASE_URL}/ad/${ad.ad_id}`,
    "seller": {
      "@type": "Person",
      "name": ad.user_name || "Vânzător",
      "url": ad.user_id ? `${BASE_URL}/seller/${ad.user_id}` : undefined
    },
    "itemCondition": ad.condition === "new"
      ? "https://schema.org/NewCondition"
      : "https://schema.org/UsedCondition",
    "shippingDetails": shippingDetails,
    "hasMerchantReturnPolicy": hasMerchantReturnPolicy
  };
  if (ad.city_name || ad.localitate) {
    offerData.areaServed = {
      "@type": "Place",
      "name": `${ad.city_name || ad.localitate}, România`
    };
  }

  // ---- Always emit a Brand identifier (Google requires at least one global ID) ----
  // Fallback: site brand when no explicit product brand is known.
  const brand = ad.brand
    ? { "@type": "Brand", "name": String(ad.brand) }
    : { "@type": "Brand", "name": "X67 Digital Media" };

  const data = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${BASE_URL}/ad/${ad.ad_id}#product`,
    "name": ad.title,
    "description": (ad.description || "").substring(0, 5000),
    "image": (ad.images || []).slice(0, 6),
    "url": `${BASE_URL}/ad/${ad.ad_id}`,
    "sku": ad.ad_id,
    "mpn": ad.ad_id,
    "category": ad.category_name,
    "brand": brand,
    "areaServed": ad.city_name || ad.localitate
      ? { "@type": "Place", "name": `${ad.city_name || ad.localitate}, România` }
      : { "@type": "Country", "name": "România" },
    "offers": offerData
  };

  // Optional global trade identifier if the ad provides one
  if (ad.gtin) data.gtin = String(ad.gtin);
  if (ad.mpn) data.mpn = String(ad.mpn);

  // Aggregate rating from seller — only if we also have at least one real review,
  // otherwise Google flags "aggregateRating without review".
  const hasRatings = Number(ad.user_rating) > 0 && Number(ad.user_reviews) > 0;
  if (hasRatings) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": Number(ad.user_rating).toFixed(1),
      "reviewCount": Math.max(1, Number(ad.user_reviews)),
      "bestRating": 5,
      "worstRating": 1
    };
    // Emit a representative seller review so aggregateRating has a companion review
    data.review = [{
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": Number(ad.user_rating).toFixed(1),
        "bestRating": 5,
        "worstRating": 1
      },
      "author": {
        "@type": "Organization",
        "name": "Clienți X67 Digital Media"
      },
      "reviewBody": `Vânzător cu rating ${Number(ad.user_rating).toFixed(1)}/5 bazat pe ${Number(ad.user_reviews)} recenzii verificate pe X67 Digital Media.`
    }];
  }

  return <SchemaMarkup data={data} />;
}

// ============== CATEGORY (CollectionPage + ItemList) ==============
export function CategorySchema({ category, ads = [], page = 1, totalAds = 0 }) {
  if (!category) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category.name} - Anunțuri România`,
    "description": `${totalAds || ads.length} anunțuri ${category.name} din toată România. Prețuri actualizate live!`,
    "url": `${BASE_URL}/category/${category.id}${page > 1 ? `?page=${page}` : ''}`,
    "isPartOf": { "@id": `${BASE_URL}#website` },
    "about": {
      "@type": "Thing",
      "name": category.name
    },
    "mainEntity": {
      "@type": "ItemList",
      "name": `Anunțuri ${category.name}`,
      "numberOfItems": totalAds || ads.length,
      "itemListElement": ads.slice(0, 20).map((ad, i) => ({
        "@type": "ListItem",
        "position": (page - 1) * 20 + i + 1,
        "url": `${BASE_URL}/ad/${ad.ad_id}`,
        "name": ad.title,
        "image": ad.images?.[0]
      }))
    }
  };
  return <SchemaMarkup data={data} />;
}

// ============== FAQ ==============
export function FAQSchema({ faqs }) {
  if (!faqs || faqs.length === 0) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  return <SchemaMarkup data={data} />;
}

// ============== HOW-TO ==============
export function HowToSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "Cum postezi un anunț gratuit pe X67 Digital",
    "description": "Ghid pas cu pas pentru a posta un anunț gratuit pe X67 Digital Media",
    "totalTime": "PT3M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "RON",
      "value": "0"
    },
    "supply": [
      { "@type": "HowToSupply", "name": "Cont X67 Digital" },
      { "@type": "HowToSupply", "name": "Fotografii produs (opțional)" }
    ],
    "tool": [
      { "@type": "HowToTool", "name": "Smartphone sau computer" }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Creează un cont",
        "text": "Înregistrează-te gratuit folosind email-ul sau contul Google. Procesul durează sub 1 minut.",
        "url": `${BASE_URL}/auth`
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "Alege categoria",
        "text": "Selectează categoria potrivită: Auto, Imobiliare, Electronice, Locuri de muncă, Modă, Servicii sau Animale.",
        "url": `${BASE_URL}/create-ad`
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Completează detaliile cu AI",
        "text": "Adaugă titlu, descriere, preț, locație și fotografii. Folosește butonul 'Completează cu AI' pentru a genera automat descrierea din poze.",
        "url": `${BASE_URL}/create-ad`
      },
      {
        "@type": "HowToStep",
        "position": 4,
        "name": "Publică anunțul",
        "text": "Apasă butonul de publicare. Anunțul va fi vizibil instant și verificat în câteva minute."
      }
    ]
  };
  return <SchemaMarkup data={data} />;
}

// ============== ARTICLE (Blog) ==============
export function ArticleSchema({ post }) {
  if (!post) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt || (post.content || "").replace(/<[^>]+>/g, "").substring(0, 200),
    "image": post.cover_image ? [post.cover_image] : undefined,
    "datePublished": post.created_at,
    "dateModified": post.updated_at || post.created_at,
    "author": {
      "@type": post.author_id ? "Person" : "Organization",
      "name": post.author_name || "X67 Digital Media",
      "url": post.author_id ? `${BASE_URL}/seller/${post.author_id}` : BASE_URL
    },
    "publisher": {
      "@id": `${BASE_URL}#organization`
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${BASE_URL}/blog/${post.slug || post.post_id}`
    },
    "keywords": Array.isArray(post.tags) ? post.tags.join(", ") : (post.tags || ""),
    "articleSection": post.category || "General",
    "wordCount": (post.content || "").replace(/<[^>]+>/g, "").split(/\s+/).length,
    "inLanguage": "ro-RO"
  };
  return <SchemaMarkup data={data} />;
}

// ============== Person / Seller Profile ==============
export function PersonSchema({ user, ads = [], rating, reviews = 0 }) {
  if (!user) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": user.name || "Vânzător X67",
    "url": `${BASE_URL}/seller/${user.user_id}`,
    "image": user.picture || undefined,
    "memberOf": { "@id": `${BASE_URL}#organization` },
    "knowsAbout": ads.slice(0, 5).map((a) => a.category_name).filter(Boolean)
  };
  if (rating && reviews > 0) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": Number(rating).toFixed(1),
      "reviewCount": reviews,
      "bestRating": 5,
      "worstRating": 1
    };
  }
  return <SchemaMarkup data={data} />;
}

// ============== Auction (Event) ==============
export function AuctionSchema({ auction, ad }) {
  if (!auction || !ad) return null;
  const data = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": `Licitație: ${ad.title}`,
    "description": ad.description?.substring(0, 300),
    "startDate": auction.start_at || auction.created_at,
    "endDate": auction.end_at,
    "eventStatus": auction.status === "live" ? "https://schema.org/EventScheduled" : (auction.status === "ended" ? "https://schema.org/EventCompleted" : "https://schema.org/EventScheduled"),
    "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
    "location": {
      "@type": "VirtualLocation",
      "url": `${BASE_URL}/auctions/${auction.auction_id}`
    },
    "image": ad.images?.[0],
    "offers": {
      "@type": "Offer",
      "price": auction.current_bid || auction.starting_price,
      "priceCurrency": ad.currency || "RON",
      "availability": "https://schema.org/InStock",
      "url": `${BASE_URL}/auctions/${auction.auction_id}`
    },
    "organizer": { "@id": `${BASE_URL}#organization` }
  };
  return <SchemaMarkup data={data} />;
}

// ============== Speakable (AEO — Voice search & Google Assistant) ==============
export function SpeakableSchema({ cssSelectors = ["h1", "[data-speakable]"] }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": cssSelectors
    }
  };
  return <SchemaMarkup data={data} />;
}

export default SchemaMarkup;
